import { motion } from 'framer-motion';
import {
  Bell,
  CircleAlert,
  PartyPopper,
  RefreshCw,
  UserRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAlertActions, useAlerts } from '../../contexts/AlertContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { cn } from '../../lib/utils';
import { Button } from '../Button';
import {
  listItem,
  PortalCard,
  PortalPage,
  PortalPageHeader,
  Skeleton,
  SkeletonGroup,
} from '../portal';

interface AlertItem {
  id: string;
  type: 'review' | 'milestone' | 'error';
  title: string;
  description: string;
  timestamp: string;
  isRead: boolean;
  actionUrl?: string | null;
}

const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, {
  numeric: 'auto',
});

function formatRelativeTime(value: string | number | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  if (!Number.isFinite(diffSeconds)) {
    return 'Unknown';
  }

  if (Math.abs(diffSeconds) < 60) {
    return relativeTimeFormatter.format(diffSeconds, 'second');
  }

  const diffMinutes = Math.round(diffSeconds / 60);
  if (Math.abs(diffMinutes) < 60) {
    return relativeTimeFormatter.format(diffMinutes, 'minute');
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 24) {
    return relativeTimeFormatter.format(diffHours, 'hour');
  }

  const diffDays = Math.round(diffHours / 24);
  if (Math.abs(diffDays) < 30) {
    return relativeTimeFormatter.format(diffDays, 'day');
  }

  const diffMonths = Math.round(diffDays / 30);
  if (Math.abs(diffMonths) < 12) {
    return relativeTimeFormatter.format(diffMonths, 'month');
  }

  return relativeTimeFormatter.format(Math.round(diffDays / 365), 'year');
}

// Map backend alert types to frontend display types
const mapAlertType = (
  backendType: string,
): 'review' | 'milestone' | 'error' => {
  const typeMap: Record<string, 'review' | 'milestone' | 'error'> = {
    // KYB types
    KybSubmitted: 'milestone',
    KybUnderReview: 'review',
    KybApproved: 'milestone',
    KybRejected: 'error',
    KybRequiresAdditionalInfo: 'error',
    // Dapp types
    DappSubmittedForReview: 'review',
    DappApproved: 'milestone',
    DappRejected: 'error',
    DappPublished: 'milestone',
    // Release types
    ReleaseProcessingComplete: 'milestone',
    ReleaseProcessingFailed: 'error',
    ReleaseSubmittedForReview: 'review',
    ReleaseApproved: 'milestone',
    ReleaseRejected: 'error',
    ReleasePublished: 'milestone',
    // Account types
    EmailVerified: 'milestone',
    AccountApproved: 'milestone',
    AccountSuspended: 'error',
  };
  return typeMap[backendType] || 'review';
};

const AlertIcon = ({ type }: { type: AlertItem['type'] }) => {
  switch (type) {
    case 'review':
      return (
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-600">
          <UserRound className="size-5 text-white" />
        </div>
      );
    case 'milestone':
      return (
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-seeker-800">
          <PartyPopper className="size-5 text-orange-400" />
        </div>
      );
    case 'error':
      return (
        <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-seeker-800">
          <CircleAlert className="size-5 text-rose-400" />
        </div>
      );
  }
};

const AlertItemComponent = ({
  alert,
  onMarkAsRead,
}: {
  alert: AlertItem;
  onMarkAsRead: (id: string) => void;
}) => {
  const openAlertAction = () => {
    if (!alert.isRead) {
      onMarkAsRead(alert.id);
    }
    if (alert.actionUrl) {
      window.location.href = alert.actionUrl;
    }
  };

  return (
    <button
      type="button"
      className={cn(
        'flex w-full cursor-pointer items-start justify-between gap-4 rounded-lg p-4 text-left transition-colors duration-200 hover:bg-seeker-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70',
        alert.isRead ? 'bg-seeker-800' : 'bg-seeker-900',
      )}
      onClick={openAlertAction}
    >
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <AlertIcon type={alert.type} />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-col">
            <span
              className="truncate text-sm font-bold leading-5 text-white"
              title={alert.title}
            >
              {alert.title}
            </span>
            <p className="line-clamp-1 text-sm leading-5 text-seeker-400">
              {alert.description}
            </p>
          </div>
          <span className="text-xs leading-4 text-seeker-400">
            {alert.timestamp}
          </span>
        </div>
      </div>
      {/* Slot is always reserved so rows don't reflow when the dot fades out. */}
      <span
        aria-hidden="true"
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full bg-blue-400 transition-opacity duration-200',
          alert.isRead ? 'opacity-0' : 'opacity-100',
        )}
      />
      {!alert.isRead && <span className="sr-only">Unread</span>}
    </button>
  );
};

/** Mirrors five alert rows: 32px icon, 20px title + 20px line, 16px timestamp. */
function AlertsSkeleton() {
  return (
    <SkeletonGroup label="Loading alerts" className="flex flex-col gap-2">
      {[0, 1, 2, 3, 4].map((index) => (
        <div key={index} className="flex items-start gap-4 rounded-lg p-4">
          <Skeleton className="size-8 shrink-0" rounded="lg" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-col">
              <div className="flex h-5 items-center">
                <Skeleton className="h-3.5 w-3/5" />
              </div>
              <div className="flex h-5 items-center">
                <Skeleton className="h-3.5 w-4/5" />
              </div>
            </div>
            <div className="flex h-4 items-center">
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
          <span aria-hidden="true" className="mt-1.5 size-2 shrink-0" />
        </div>
      ))}
    </SkeletonGroup>
  );
}

export const DashboardAlerts = () => {
  usePageTitle('Alerts | Publishing Portal');
  const {
    alerts: contextAlerts,
    totalAlerts,
    isLoading,
    refetchAlerts,
  } = useAlerts();
  const { markAsRead, markAllAsRead } = useAlertActions();
  const [offset, setOffset] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const limit = 20;

  const alerts: AlertItem[] = contextAlerts
    .slice(offset, offset + limit)
    .map((alert) => ({
      id: alert.id,
      type: mapAlertType(alert.type),
      title: alert.title,
      description: alert.message,
      timestamp: formatRelativeTime(alert.created),
      isRead: !!alert.readAt,
      actionUrl: alert.actionUrl,
    }));

  const total = totalAlerts;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetchAlerts();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  // Automatically mark all alerts as read after viewing the page
  useEffect(() => {
    const hasUnreadAlerts = alerts.some((alert) => !alert.isRead);

    if (hasUnreadAlerts && !isLoading) {
      // This allows users to see which alerts were unread
      const timer = setTimeout(async () => {
        await markAllAsRead();
      }, 2000); // 2 second delay to let users see unread indicators

      return () => clearTimeout(timer);
    }
  }, [alerts, isLoading, markAllAsRead]);

  const handleMarkAsRead = async (alertId: string) => {
    await markAsRead(alertId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  return (
    <PortalPage>
      <PortalPageHeader
        icon={<Bell />}
        title="Alerts"
        actions={
          <>
            <Button
              variant="ghostMuted"
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label="Refresh alerts"
              title="Refresh alerts"
              className="size-10 px-0"
            >
              <RefreshCw
                className={cn('size-4', isRefreshing && 'animate-spin')}
              />
            </Button>
            {alerts.some((a) => !a.isRead) && (
              <Button variant="outlineMuted" onClick={handleMarkAllAsRead}>
                Mark all as read
              </Button>
            )}
          </>
        }
      />

      <div className="mx-auto w-full max-w-[640px]">
        {isLoading ? (
          <AlertsSkeleton />
        ) : alerts.length === 0 ? (
          <PortalCard className="motion-enter flex flex-col items-center px-6 py-16 text-center">
            <Bell className="mb-4 size-12 text-seeker-500" />
            <h3 className="mb-2 text-lg font-semibold text-white">
              No alerts yet
            </h3>
            <p className="text-sm text-seeker-400">
              You'll see notifications here when there are updates about your
              apps and account.
            </p>
          </PortalCard>
        ) : (
          <div className="flex flex-col gap-2">
            {/* Keyed on alert id: the stagger plays once per row, never on refetch or read-state changes. */}
            {alerts.map((alert, index) => (
              <motion.div key={alert.id} {...listItem(index)}>
                <AlertItemComponent
                  alert={alert}
                  onMarkAsRead={handleMarkAsRead}
                />
              </motion.div>
            ))}
            {total > limit && (
              <div className="mt-4 flex items-center justify-center gap-4">
                <Button
                  variant="outlineMuted"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="h-9"
                >
                  Previous
                </Button>
                <span className="text-sm tabular-nums text-seeker-400">
                  {offset + 1}-{Math.min(offset + limit, total)} of {total}
                </span>
                <Button
                  variant="outlineMuted"
                  onClick={() => setOffset((prev) => prev + limit)}
                  disabled={offset + limit >= total}
                  className="h-9"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </PortalPage>
  );
};
